import React, { useState, useEffect } from 'react';
import usePush, { isInstalled } from '../usePush';
import track from '../track';

/**
 * The ask.
 *
 * Notifications were reachable only through a small grey button in a subtitle
 * row on the Weekly tab — and members land on Home. Nothing ever asked, so
 * nobody ever subscribed: six active members, zero subscriptions, zero
 * notifications ever sent. The feature was built, wired, deployed, and
 * invisible.
 *
 * This sits on the home page, states what it is for, and is dismissible. It is
 * deliberately not a modal: a pick'em app nagging you before you've picked
 * anything is how people turn things off for good.
 */
const DISMISS_KEY = 'notify_nudge_dismissed';

export default function NotifyBanner() {
  const { state, loading, subscribe } = usePush();
  const [dismissed, setDismissed] = useState(!!localStorage.getItem(DISMISS_KEY));

  // Nothing to say once they're set up, if they've said no at the browser level
  // (only iOS Settings can undo that), or on a device that genuinely can't.
  const hidden = dismissed ||
    // Until the device has been worked out, show nothing and count nothing —
    // this is the frame that used to fire "saw the ask" for everybody.
    state === 'checking' ||
    state === 'subscribed' || state === 'denied' ||
    state === 'unsupported' ||
    // An iPhone in a Safari tab is capable but not yet — the install prompt owns
    // that conversation, so don't run a competing one.
    state === 'needs_install';

  // Count the ask only when it is genuinely on screen. "We asked and they said
  // no" and "we never asked" are different problems with different fixes, and a
  // count taken at render time is the only one that can tell them apart.
  useEffect(() => { if (!hidden) track('push_prompt_seen'); }, [hidden]);

  if (hidden) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div style={{
      margin: '0 0 18px', padding: '14px 16px', borderRadius: 12,
      background: 'linear-gradient(135deg, rgba(1,51,105,0.35), rgba(213,10,10,0.10))',
      border: '1px solid rgba(127,179,255,0.3)',
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap'
    }}>
      <span style={{ fontSize: 24, lineHeight: 1 }}>🔔</span>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: 0.4 }}>
          DON'T MISS A WEEK
        </div>
        <div style={{ fontSize: 13, color: '#99a', marginTop: 3, lineHeight: 1.45 }}>
          A nudge before picks lock, your result when the week ends, and a shout
          when you win the pool.{!isInstalled() && ' Works best with the app on your home screen.'}
        </div>
      </div>
      <button
        onClick={subscribe}
        disabled={loading}
        style={{
          padding: '10px 20px', borderRadius: 8, border: 'none',
          background: loading ? '#555' : 'linear-gradient(135deg,#D50A0A,#a00808)',
          color: '#fff', fontSize: 13, fontWeight: 700,
          fontFamily: "'Oswald',sans-serif", letterSpacing: 0.5, cursor: 'pointer'
        }}
      >{loading ? 'ENABLING…' : 'TURN ON'}</button>
      <button
        onClick={dismiss}
        style={{ padding: '10px 12px', background: 'transparent', border: 'none', color: '#556', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
      >Not now</button>
    </div>
  );
}
